import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Skeleton,
  Chip,
  Rating,
  IconButton,
} from '@mui/material';
import {
  Search,
  ShoppingCart,
  FavoriteBorder,
  Favorite,
  FilterList,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addToCart, addToLocalCart } from '../../store/slices/cartSlice';
import { fetchProducts, fetchCategories } from '../../store/slices/productSlice';
import { toast } from 'react-toastify';
import { RootState, Product, Category } from '../../types';
import { AppDispatch } from '../../store/store';
import PriceDisplay from '../../components/common/PriceDisplay';
import { getImageUrl } from '../../utils/imageUtils';
import { useSettings } from '../../contexts/SettingsContext';
import analytics from '../../utils/analytics';

interface ProductCardProps {
  product: Product;
}

const Shop: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const { products, categories, loading, totalPages } = useSelector((state: RootState) => state.products);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [page, setPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || '');
  const [category, setCategory] = useState<string>(searchParams.get('category') || '');
  const [sortBy, setSortBy] = useState<string>('title');
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');
  const [favorites, setFavorites] = useState<number[]>([]);
  const { currencySettings } = useSettings();

  // Product prices are stored in BGN; convert the filter input to BGN when the UI is showing EUR
  const isEurOnly = currencySettings.currency_display_mode === 'eur_only';
  const priceCurrencySymbol = isEurOnly ? '€' : 'лв.';
  const toBgn = (value: string) => {
    const parsed = parseFloat(value);
    return isEurOnly ? parsed * currencySettings.bgn_to_eur_rate : parsed;
  };

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    const params = {
      page: page - 1,
      size: 12,
      searchTerm,
      category,
      sortBy,
      ...(minPrice && { minPrice: toBgn(minPrice) }),
      ...(maxPrice && { maxPrice: toBgn(maxPrice) }),
    };
    dispatch(fetchProducts(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page, searchTerm, category, sortBy, minPrice, maxPrice, isEurOnly, currencySettings.bgn_to_eur_rate]);

  useEffect(() => {
    if (products.length > 0) {
      analytics.viewItemList(products, 'Shop');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const handleAddToCart = (product: Product) => {
    if (isAuthenticated) {
      // Use backend API for authenticated users
      dispatch(addToCart({ productId: product.id, quantity: 1 }));
    } else {
      // Use local sessionStorage for guest users
      dispatch(addToLocalCart({ product, quantity: 1 }));
    }
    analytics.addToCart(product, 1);
    toast.success(t('shop.addedToCart', { title: product.title }));
  };

  const handleToggleFavorite = (productId: number) => {
    if (!isAuthenticated) {
      toast.info(t('shop.loginToAddFavorites'));
      navigate('/login');
      return;
    }
    if (favorites.includes(productId)) {
      setFavorites(favorites.filter((id) => id !== productId));
    } else {
      setFavorites([...favorites, productId]);
    }
  };

  const handleProductClick = (productId: number) => {
    navigate(`/products/${productId}`);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
    return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="200"
          image={getImageUrl(product.imageUrl)}
          alt={product.title}
          sx={{ cursor: 'pointer', objectFit: 'contain' }}
          onClick={() => handleProductClick(product.id)}
        />
        {/* Discount chip can be added later if discount field is available */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            backgroundColor: 'white',
            '&:hover': { backgroundColor: 'white' },
          }}
          size="small"
          onClick={() => handleToggleFavorite(product.id)}
        >
          {favorites.includes(product.id) ? (
            <Favorite color="error" />
          ) : (
            <FavoriteBorder />
          )}
        </IconButton>
      </Box>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography
          gutterBottom
          variant="h6"
          component="div"
          sx={{
            cursor: 'pointer',
            '&:hover': { color: 'primary.main' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
          onClick={() => handleProductClick(product.id)}
        >
          {product.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {product.categories?.[0]?.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Rating value={product.averageRating || 0} readOnly size="small" />
          <Typography variant="body2" sx={{ ml: 1 }}>
            ({product.reviewCount || 0})
          </Typography>
        </Box>
        <PriceDisplay
          price={product.price}
          discountPrice={product.discountPrice}
          priceEur={product.priceEur}
          discountPriceEur={product.discountPriceEur}
          variant="h6"
          layout="stacked"
        />
        {product.stock < 10 && product.stock > 0 && (
          <Typography variant="caption" color="warning.main">
            {t('shop.onlyLeftInStock', { count: product.stock })}
          </Typography>
        )}
      </CardContent>
      <CardActions>
        <Button
          fullWidth
          variant="contained"
          startIcon={<ShoppingCart />}
          onClick={() => handleAddToCart(product)}
          disabled={product.stock === 0}
        >
          {product.stock === 0 ? t('shop.outOfStock') : t('cart.addToCart')}
        </Button>
      </CardActions>
    </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('shop.title')}
      </Typography>

      {/* Filters and Search */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder={t('shop.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t('shop.category')}</InputLabel>
              <Select
                value={category}
                label={t('shop.category')}
                onChange={(e) => setCategory(e.target.value as string)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                }
              >
                <MenuItem value="">{t('shop.allCategories')}</MenuItem>
                {categories.map((cat: Category) => (
                  <MenuItem key={cat.id} value={cat.id.toString()}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t('shop.sortBy')}</InputLabel>
              <Select
                value={sortBy}
                label={t('shop.sortBy')}
                onChange={(e) => setSortBy(e.target.value as string)}
              >
                <MenuItem value="title">{t('shop.sortName')}</MenuItem>
                <MenuItem value="price">{t('shop.sortPriceLowHigh')}</MenuItem>
                <MenuItem value="price,desc">{t('shop.sortPriceHighLow')}</MenuItem>
                <MenuItem value="createdAt,desc">{t('shop.sortNewest')}</MenuItem>
                <MenuItem value="averageRating,desc">{t('shop.sortBestRated')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label={t('shop.minPrice')}
              type="number"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">{priceCurrencySymbol}</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label={t('shop.maxPrice')}
              type="number"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">{priceCurrencySymbol}</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Products Grid */}
      <Grid container spacing={3}>
        {loading
          ? Array.from(new Array(8)).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent>
                    <Skeleton />
                    <Skeleton width="60%" />
                    <Skeleton width="40%" />
                  </CardContent>
                  <CardActions>
                    <Skeleton variant="rectangular" width="100%" height={36} />
                  </CardActions>
                </Card>
              </Grid>
            ))
          : products?.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <ProductCard product={product} />
              </Grid>
            ))}
      </Grid>

      {/* No Products Message */}
      {!loading && products?.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            {t('shop.noProductsFound')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('shop.tryAdjustingFilters')}
          </Typography>
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}
    </Container>
  );
};

export default Shop;